import './Table.css';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { type Point, isNonNullable } from '@ipfabric/utils';
import { is } from '@ipfabric/utils/fp/comparison';
import { maybePropN, prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import {
  LazyRowContainer,
  type RowDragState,
} from 'components/LazyRowContainer/LazyRowContainer';
import { useLazyRowContainerScrollViewAdapter } from 'components/LazyRowContainer/useLazyRowContainerScrollViewAdapter';
import { ResizeObserverContainer } from 'components/ResizeObserverContainer/ResizeObserverContainer';
import { TableHeader } from 'components/Table/components/TableHeader';
import { TableStickyContainer } from 'components/Table/components/TableStickyContainer';
import { makeRenderCell } from 'components/Table/components/makeRenderCell';
import { useAutoSizedColumns } from 'components/Table/components/useAutoSizedColumns';
import {
  TableContext,
  useTableContextSelectors,
} from 'components/Table/model/TableModel';
import { handleRequestRowRange } from 'components/Table/model/commands/handleRequestRowRange';
import { setTableContainerSize } from 'components/Table/model/commands/setTableContainerSize';
import { selectRenderedColumnWidths } from 'components/Table/model/selectors/selectRenderedColumnWidths';
import { selectTableCellContext } from 'components/Table/model/selectors/selectTableCellContext';
import { selectVisibleColumnsByStickiness } from 'components/Table/model/selectors/selectVisibleColumnsByStickiness';
import { selectVisibleColumnsWidth } from 'components/Table/model/selectors/selectVisibleColumnsWidth';
import {
  TABLE_BOTTOM_HIDDEN_WIDTH,
  TABLE_CONTENT_BORDER_WIDTH,
  TABLE_RIGHT_HIDDEN_WIDTH,
  TABLE_ROW_HEIGHT,
} from 'components/Table/tableSettings';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { flow } from 'fp-ts/lib/function';
import type * as React from 'react';
import type { CSSProperties, FC, ReactNode } from 'react';
import type { JSX } from 'react';
import { FormattedMessage } from 'react-intl';
import { simpleTypeMemo } from 'utils/react/simpleTypeMemo';
import { withModelProvider } from 'utils/react/withModelProvider';
import { setContentHeight } from '../model/commands/setContentHeight';
import { setTableContextMenu } from '../model/commands/setTableContextMenu';
import { AppliedFilters } from './AppliedFilters';
import { TableContextMenu } from './TableContextMenu';
import { TableRowDetail } from './TableRowDetail/TableRowDetail';
import { TableSettings } from './TableSettings/TableSettings';
import { TableTitle } from './TableTitle';

/**
 * Props passed through from Table to TableView
 */
export interface TableViewProps {
  /** Setting this prop turns on auto-stretching the table up to given height */
  autoHeightLimit?: number;
  /** By default, table is rendered with round borders and shadow */
  borderless?: boolean;
  className?: string;
  /** Row height in px; defaults to table standard */
  rowHeight?: number;
  headerAddon?: ReactNode;
  hideFooter?: boolean;
  disableTableRowDetail?: boolean;
  tableRowDetailContainer?: HTMLElement | null;
  /** Visual state for drag-and-drop row reordering */
  rowDragState?: RowDragState;
  onRowMouseMove?: (rowIndex: number, e: React.MouseEvent) => void;
  onRowMouseLeave?: () => void;
  hideTitle?: boolean;
}

type Props = TableViewProps;

/** Maximum number of rows we can render that were tested to not cause trouble in major browsers */
const MAX_SAFE_ROW_COUNT = 1e5;

const capRowCount = (rowCount: number | null) =>
  rowCount === null ? null : Math.min(rowCount, MAX_SAFE_ROW_COUNT);
type TableContentStyle = CSSProperties & Record<'--table-row-height', string>;

const HorizontallyFixedTableContent: FC<{
  children: ReactNode;
  className?: string;
}> = (props) => {
  const ctxProps = useTableContextSelectors({
    tableContainerWidth: maybePropN('tableContainerSize', 'width'),
  });

  const BorderLeftRightWidth = TABLE_CONTENT_BORDER_WIDTH * 2;
  const width =
    ctxProps.tableContainerWidth !== null
      ? ctxProps.tableContainerWidth - BorderLeftRightWidth
      : undefined;

  return (
    <div
      className={classNames('p-2', 'sticky', 'left-0', props.className)}
      style={{ width }}
    >
      {props.children}
    </div>
  );
};

const BareTableView = (props: Props): JSX.Element => {
  const ctxProps = useTableContextSelectors({
    contentHeight: prop('contentHeight'),
    data: prop('data'),
    dataRequestNumber: prop('dataRequestNumber'),
    dispatch: prop('dispatch'),
    loading: prop('loading'),
    loadingSettings: flow(prop('loadingSettings'), is('LOADING')),
    loadedSettings: prop('loadedSettings'),
    rowCount: prop('rowCount'),
    rowCountMore: prop('rowCountMore'),
    rowIdField: prop('rowIdField'),
    showOnlyIntentResultsOnColumns: prop('showOnlyIntentResultsOnColumns'),
    selectedRows: (s) => s.selectedRows,
    tableCellContext: selectTableCellContext,
    tableContainerRef: prop('tableContainerRef'),
    tableContextMenu: prop('tableContextMenu'),
    renderedColumnWidths: selectRenderedColumnWidths,
    visibleColumnsByStickiness: selectVisibleColumnsByStickiness,
    visibleColumnsWidth: selectVisibleColumnsWidth,
    tableRowDetail: prop('tableRowDetail'),
    noDataBox: prop('noDataBox'),
  });
  const scrollAdapter = useLazyRowContainerScrollViewAdapter();
  const tableMsg = useLocale().table;

  useAutoSizedColumns();

  const makeOpenContextMenu = (index: number) => (position: Point) => {
    ctxProps.dispatch(setTableContextMenu, { state: { index, position } });
  };

  const renderRow = (index: number) => {
    const renderCell = makeRenderCell(
      ctxProps.data[index],
      ctxProps.renderedColumnWidths,
      ctxProps.tableCellContext,
      index,
      props.disableTableRowDetail || ctxProps.data[index] === undefined
        ? null
        : makeOpenContextMenu(index),
      ctxProps.showOnlyIntentResultsOnColumns,
    );
    return (
      <>
        {ctxProps.visibleColumnsByStickiness.sticky.length > 0 && (
          <TableStickyContainer>
            {ctxProps.visibleColumnsByStickiness.sticky.map(renderCell)}
          </TableStickyContainer>
        )}
        {ctxProps.visibleColumnsByStickiness.scrolling.map(renderCell)}
      </>
    );
  };

  const rowHeight = props.rowHeight ?? TABLE_ROW_HEIGHT;
  const effectiveRowCount = capRowCount(
    ctxProps.rowCount === undefined
      ? ctxProps.data.length
      : // null intentionally passed through as is to indicate loading
        ctxProps.rowCount,
  );
  const disableStickyHeader =
    !ctxProps.loading &&
    ctxProps.noDataBox !== null &&
    ctxProps.noDataBox !== undefined &&
    effectiveRowCount === 0;

  const getRowContainerClassName = (rowIndex: number) => {
    const selected =
      ctxProps.rowIdField &&
      ctxProps.selectedRows.has(
        // data are untyped
        (ctxProps.data[rowIndex] as any)?.[ctxProps.rowIdField],
      );
    return classNames('ipf-table__row', selected && 'ipf-table__row--selected');
  };

  const tableContentStyle: TableContentStyle = {
    ...(props.autoHeightLimit === undefined
      ? // Fits like 6 rows + columns header with filters.
        // Also, with this height, Select dropdown in header filters fits
        // the scroll area and does not get cropped due to overflow.
        // Important: min table height must stay stable even when custom rowHeight is used,
        // otherwise setting larger rowHeight would incorrectly stretch the whole table.
        { minHeight: TABLE_ROW_HEIGHT * 8 }
      : { height: Math.min(props.autoHeightLimit, ctxProps.contentHeight) }),
    borderRightWidth: TABLE_CONTENT_BORDER_WIDTH,
    borderLeftWidth: TABLE_CONTENT_BORDER_WIDTH,
    '--table-row-height': `${rowHeight}px`,
  };

  // loading settings or data (= when lazy row container shows loader instead of content)
  // unused now but intentionally keeping there as a non-obvious piece of knowledge
  // const loading = ctxProps.loadingSettings || ctxProps.loading || ctxProps.rowCount === null;

  return (
    <div
      className={classNames(
        'flex relative',
        'text-default bg-default',
        !props.borderless && 'rounded-lg shadow',
        props.className,
      )}
      ref={ctxProps.tableContainerRef}
    >
      <div className='ipf-table-resize-handle-container absolute inset-0 pointer-events-none [&>*]:pointer-events-auto overflow-hidden' />
      <TableSettings />
      <TableContextMenu />
      <div className='flex w-full flex-1 flex-col'>
        {!props.hideTitle && <TableTitle borderless={props.borderless} />}
        {props.headerAddon && (
          <div className='has-[+.ipf-table-applied-filters]:mb-sm'>
            {props.headerAddon}
          </div>
        )}
        <AppliedFilters />
        <ResizeObserverContainer
          onResize={(element) => {
            ctxProps.dispatch(setTableContainerSize, {
              size: {
                width: element.clientWidth,
                height: element.clientHeight,
              },
            });
          }}
          className={classNames(
            'ipf-table__content',
            props.autoHeightLimit === undefined && 'flex-1',
          )}
          style={tableContentStyle}
        >
          {ctxProps.loadedSettings ? (
            <ScrollView
              className={classNames(
                'flex-1',
                ctxProps.tableContextMenu !== null && 'pointer-events-none', // disable scroll with context menu
                isNonNullable(props.rowDragState?.draggedIndex) &&
                  'ipf-table--dragging',
              )}
              {...scrollAdapter.scrollViewProps}
              styleCorrections={{
                // if this correction will be removed, then you have to adjust availableWidth in useAutoSizeTableColumns
                marginRight:
                  -TABLE_CONTENT_BORDER_WIDTH - TABLE_RIGHT_HIDDEN_WIDTH,
                marginBottom: -TABLE_BOTTOM_HIDDEN_WIDTH,
              }}
            >
              <LazyRowContainer
                bodyWrapperClassName='ipf-table__body-wrapper min-w-full'
                bodyWrapperStyle={{ width: ctxProps.visibleColumnsWidth }}
                dataReloadTrigger={ctxProps.dataRequestNumber}
                defaultRowHeight={rowHeight}
                fixedHeader={
                  !ctxProps.loadingSettings && (
                    <TableHeader disableSticky={disableStickyHeader} />
                  )
                }
                loading={ctxProps.loadingSettings || ctxProps.loading}
                noStretchingRows
                onContentHeightChange={(height) =>
                  ctxProps.dispatch(setContentHeight, { height })
                }
                onRequestRowRange={(startIndex, endIndex) => {
                  ctxProps.dispatch(handleRequestRowRange, {
                    rowRange: { startIndex, endIndex },
                  });
                }}
                renderEmptyContent={() => (
                  <HorizontallyFixedTableContent>
                    <div data-testid='table-no-data'>
                      {ctxProps.noDataBox ?? tableMsg.noData}
                    </div>
                  </HorizontallyFixedTableContent>
                )}
                renderLoader={() => (
                  <HorizontallyFixedTableContent>
                    <LoaderAndText noVerticalMargin />
                  </HorizontallyFixedTableContent>
                )}
                rowDragState={props.rowDragState}
                onRowMouseMove={props.onRowMouseMove}
                onRowMouseLeave={props.onRowMouseLeave}
                renderRow={renderRow}
                rowContainerClassName={getRowContainerClassName}
                rowCount={effectiveRowCount}
                scrollContainerAdapter={scrollAdapter.scrollContainerAdapter}
              />
            </ScrollView>
          ) : (
            <LoaderAndText />
          )}
        </ResizeObserverContainer>
        {!props.hideFooter && (
          <div className='ipf-table__footer'>
            <div className='text-sm font-normal text-default'>
              {(() => {
                const capped =
                  (ctxProps.rowCount ?? ctxProps.data.length) >
                  MAX_SAFE_ROW_COUNT;
                if (ctxProps.rowCount === null) {
                  return tableMsg.itemsTotalLoading;
                }
                if (ctxProps.rowCountMore) {
                  return (
                    <>
                      <FormattedMessage
                        id='table.itemsTotalMore'
                        defaultMessage={tableMsg.itemsTotalMore}
                        values={{ count: ctxProps.data.length }}
                      />
                      <TooltipIcon
                        content={
                          capped ? (
                            <FormattedMessage
                              id='table.itemsTotalMoreCappedTooltip'
                              defaultMessage={
                                tableMsg.itemsTotalMoreCappedTooltip
                              }
                              values={{ cap: MAX_SAFE_ROW_COUNT }}
                            />
                          ) : (
                            tableMsg.itemsTotalMoreTooltip
                          )
                        }
                        iconProps={{
                          iconStyle: 'solid',
                          inline: true,
                        }}
                        size='sm'
                        className='ml-2'
                      />
                    </>
                  );
                }
                return (
                  <>
                    <FormattedMessage
                      id='table.itemsTotal'
                      defaultMessage={
                        capped ? tableMsg.itemsTotalCapped : tableMsg.itemsTotal
                      }
                      values={{
                        count: ctxProps.rowCount ?? ctxProps.data.length,
                        cap: MAX_SAFE_ROW_COUNT,
                      }}
                    />
                    {capped && (
                      <TooltipIcon
                        content={
                          <FormattedMessage
                            id='table.itemsTotalCappedTooltip'
                            defaultMessage={tableMsg.itemsTotalCappedTooltip}
                            values={{ cap: MAX_SAFE_ROW_COUNT }}
                          />
                        }
                        iconProps={{
                          iconStyle: 'solid',
                          inline: true,
                        }}
                        size='sm'
                        className='ml-2'
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
      <TableRowDetail tableRowDetailContainer={props.tableRowDetailContainer} />
    </div>
  );
};

export const TableView = withModelProvider(TableContext)(
  simpleTypeMemo(BareTableView),
);
